import { useMemo, useState } from "react";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@semo/shared-ui";
import { Plus, Trash2, ClipboardList } from "@semo/icons";

/**
 * paperCode = 164 / 188 / 195 / 222 공용 Confirm + 명단 테이블 모달.
 *
 * 1단계 (confirm): "있음 / 해당없음" 선택
 * 2단계 (있음 선택 시): 명단 테이블 입력 → 제출
 * 2단계 (해당없음 선택 시): rows=[] 로 즉시 제출 (c-market 은 "해당없음" 을 빈 배열로 표현)
 *
 * 백엔드 c-market body 키는 retireeRoster 로 통일. rows 원소 필드:
 *   { name*, position*, joinDate?, retireType?, servicePeriod?, etc? }
 *   (* required, ? nullable)
 *
 * 각 paperCode 는 UI 컬럼 스펙(라벨 · 필수 · placeholder) 만 다르며 body key 는 동일.
 * 스펙에 없는 필드는 undefined 로 두면 자동으로 body 에 null 로 매핑된다.
 */

type RowKey =
  | "name"
  | "position"
  | "joinDate"
  | "retireType"
  | "servicePeriod"
  | "etc";

interface ColumnSpec {
  key: RowKey;
  label: string;
  required?: boolean;
  placeholder?: string;
  suffix?: string;
}

interface PaperSpec {
  title: string;
  description: string;
  confirmQuestion: string;
  columns: ColumnSpec[];
}

// 스펙: 사용자 요구사항 기반. 서류명은 정식 문구로 교체 필요 시 개별 조정.
const PAPER_SPECS: Record<number, PaperSpec> = {
  164: {
    title: "퇴직자 영입현황 확인",
    description:
      "본 계약과 관련하여 발주기관 소속 퇴직자를 영입한 사실이 있는지 확인해주세요. 영입한 경우 아래 명단을 입력하면 '퇴직자 영입현황 확인서'에 자동으로 반영됩니다.",
    confirmQuestion: "영입한 퇴직자가 있나요?",
    columns: [
      { key: "name", label: "성명", required: true, placeholder: "홍길동" },
      { key: "position", label: "직급", required: true, placeholder: "부장" },
      { key: "joinDate", label: "입사일", placeholder: "YYYY-MM-DD" },
      {
        key: "retireType",
        label: "직급 (간부·보직자)",
        placeholder: "예: 3급",
      },
      { key: "servicePeriod", label: "근무기간", suffix: "개월" },
      { key: "etc", label: "비고" },
    ],
  },
  188: {
    title: "업체 정보 확인",
    description:
      "본 계약과 관련하여 해당 서류에 반영할 내용이 있는지 확인해주세요. 있는 경우 아래 명단을 입력하면 서류에 자동으로 반영됩니다.",
    confirmQuestion: "해당 사항이 있나요?",
    columns: [
      // 소속 · 임용일은 c-market 선행 작업 후 추가 예정 → 현재 UI 미노출
      { key: "name", label: "성명", required: true, placeholder: "홍길동" },
      { key: "position", label: "직위", required: true, placeholder: "과장" },
      { key: "etc", label: "비고" },
    ],
  },
  195: {
    title: "업체 정보 확인",
    description:
      "본 계약과 관련하여 해당 서류에 반영할 내용이 있는지 확인해주세요. 있는 경우 아래 명단을 입력하면 서류에 자동으로 반영됩니다.",
    confirmQuestion: "해당 사항이 있나요?",
    columns: [
      { key: "name", label: "성명", required: true, placeholder: "홍길동" },
      { key: "position", label: "직위", required: true, placeholder: "과장" },
      { key: "joinDate", label: "입사일", placeholder: "YYYY-MM-DD" },
      { key: "servicePeriod", label: "근무기간", suffix: "개월" },
      { key: "etc", label: "비고" },
    ],
  },
  222: {
    title: "업체 정보 확인",
    description:
      "본 계약과 관련하여 해당 서류에 반영할 내용이 있는지 확인해주세요. 있는 경우 아래 명단을 입력하면 서류에 자동으로 반영됩니다.",
    confirmQuestion: "해당 사항이 있나요?",
    columns: [
      // 퇴직일은 c-market 선행 작업 후 추가 예정 → 현재 UI 미노출
      { key: "name", label: "성명", required: true, placeholder: "홍길동" },
      { key: "position", label: "직위", required: true, placeholder: "과장" },
      { key: "joinDate", label: "입사일", placeholder: "YYYY-MM-DD" },
      { key: "retireType", label: "직급 (직위)", placeholder: "예: 3급" },
    ],
  },
};

type Row = Partial<Record<RowKey, string>>;

interface OptionalRosterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paperCode: number;
  /**
   * "해당없음" 은 빈 배열([])로 전달. null 은 사용하지 않음(c-market 은 빈 배열 = 해당없음).
   */
  onSubmit: (
    rows: Array<{
      name: string;
      position: string;
      joinDate?: string | null;
      retireType?: string | null;
      servicePeriod?: string | null;
      etc?: string | null;
    }>,
  ) => Promise<void>;
}

export function OptionalRosterModal({
  open,
  onOpenChange,
  paperCode,
  onSubmit,
}: OptionalRosterModalProps) {
  const spec = PAPER_SPECS[paperCode];
  const [step, setStep] = useState<"confirm" | "form">("confirm");
  const [rows, setRows] = useState<Row[]>([{}]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetAndClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStep("confirm");
      setRows([{}]);
      setError(null);
    }
    onOpenChange(nextOpen);
  };

  const submitNone = async () => {
    setIsSubmitting(true);
    try {
      // 해당없음 = 빈 배열 (c-market spec).
      await onSubmit([]);
      resetAndClose(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitRows = async () => {
    if (!spec) return;
    setError(null);
    // 필수 필드 검증
    for (const row of rows) {
      for (const col of spec.columns) {
        if (col.required && !row[col.key]?.trim()) {
          setError(`'${col.label}' 은(는) 필수입니다`);
          return;
        }
      }
    }
    setIsSubmitting(true);
    try {
      const normalized = rows.map((row) => ({
        name: (row.name ?? "").trim(),
        position: (row.position ?? "").trim(),
        joinDate: row.joinDate?.trim() || null,
        retireType: row.retireType?.trim() || null,
        servicePeriod: row.servicePeriod?.trim() || null,
        etc: row.etc?.trim() || null,
      }));
      await onSubmit(normalized);
      resetAndClose(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addRow = () => setRows((prev) => [...prev, {}]);
  const removeRow = (idx: number) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length > 0 ? next : [{}];
    });
  };
  const updateCell = (idx: number, key: RowKey, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const columns = useMemo(() => spec?.columns ?? [], [spec]);

  if (!spec) return null;

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-primary-text" />
            {spec.title}
          </DialogTitle>
          <DialogDescription>{spec.description}</DialogDescription>
        </DialogHeader>

        {step === "confirm" ? (
          <div className="my-3">
            <p className="text-sm font-semibold text-slate-800 mb-3">
              {spec.confirmQuestion}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={isSubmitting}
                onClick={() => setStep("form")}
              >
                있음
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={isSubmitting}
                onClick={() => {
                  void submitNone();
                }}
              >
                {isSubmitting ? "처리 중..." : "해당없음"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="my-2">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className="px-2 py-2 text-left font-semibold text-slate-600"
                      >
                        {col.label}
                        {col.required && (
                          <span className="text-red-500 ml-0.5">*</span>
                        )}
                      </th>
                    ))}
                    <th className="px-2 py-2 w-8" aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      {columns.map((col) => (
                        <td key={col.key} className="p-1">
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={row[col.key] ?? ""}
                              onChange={(e) =>
                                updateCell(idx, col.key, e.target.value)
                              }
                              placeholder={col.placeholder}
                              className={cn(
                                "w-full px-2 py-1.5 rounded border border-slate-200",
                                "text-xs focus:outline-none focus:border-primary/60",
                              )}
                            />
                            {col.suffix && (
                              <span className="text-2xs text-slate-500 shrink-0">
                                {col.suffix}
                              </span>
                            )}
                          </div>
                        </td>
                      ))}
                      <td className="p-1 text-center">
                        <button
                          type="button"
                          onClick={() => removeRow(idx)}
                          className="text-slate-400 hover:text-red-500 p-1 rounded"
                          aria-label="행 삭제"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addRow}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-text hover:text-primary-text"
            >
              <Plus className="w-3.5 h-3.5" />행 추가
            </button>
            {error && (
              <p className="mt-2 text-xs text-red-500">{error}</p>
            )}
          </div>
        )}

        {step === "form" && (
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => setStep("confirm")}
            >
              이전
            </Button>
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                void submitRows();
              }}
            >
              {isSubmitting ? "제출 중..." : "제출"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
